import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { AutoCompletePanelBehaviour } from '@barthauer/abc/ui-components';
import {
  BasysMapFrameService,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { Subscription } from 'rxjs';
import { worksAnimation } from '../../animations';
import {
  ComponentDataSource,
  DataSourceFilter,
  FilterOperator,
  FilterType,
} from '../../models/componentDataSource.model';
import { AppService } from '../../services/app.service';
import { FrameService } from '../../services/frame.service';
import { ViewModelHelper } from '../../shared/helpers';
import {
  IMobileWork,
  MobileWorkViewModel,
} from '../../viewModels/mobileData.viewModel';
import {
  WorkConstants,
  WorkNavigationContext,
  WorksDataContext,
  WorkViewMode,
  WorkViewPackage,
} from '../works.model';
import { WorksService } from '../works.service';

@Component({
  selector: 'app-works',
  templateUrl: './works.component.html',
  styleUrls: ['./works.component.scss'],
  animations: [worksAnimation],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class WorksComponent implements OnInit, OnDestroy, AfterViewInit {
  public res = ResourceSet.Empty;
  AppService = AppService;
  AutoCompletePanelBehaviour = AutoCompletePanelBehaviour;
  dataSource: ComponentDataSource<IMobileWork>;
  allAutocompleteItems: string[];
  console = console;
  ViewModelHelper = ViewModelHelper;
  workNavigationContext: WorkNavigationContext;
  private dataContext: WorksDataContext;
  private subscriptions: Subscription[] = [];

  @ViewChild('searchMenuTrigger', { static: false })
  searchMenuTrigger: MatMenuTrigger;

  @ViewChild('routerOutlet') routerOutlet!: RouterOutlet;
  animationState = 'WorkList';

  constructor(
    public frame: BasysMapFrameService,
    private frameService: FrameService,
    public worksService: WorksService,
    private resourceService: ResourceManagerService,
    private cdr: ChangeDetectorRef,
  ) {
    this.dataSource = new ComponentDataSource();
    this.dataContext = new WorksDataContext(worksService);

    this.subscriptions.push(
      this.frameService.onMobileWorksSent().subscribe((wvp) => {
        let navigateToFirstWork: boolean = false;
        if (this.dataContext.dataSource) this.dataContext.resetFilter();
        switch (wvp.mode) {
          case WorkViewMode.AllFilteredByEntities:
            navigateToFirstWork =
              wvp.filterEntities && wvp.filterEntities.length === 1;
            this.trySortFilteredWorks(wvp);
            this.setDataSource(wvp.allWorks);
            break;
          case WorkViewMode.AggregatedByEntityCategory:
            this.dataContext.isInAggregatedMode = true;
            navigateToFirstWork =
              (wvp.aggregatedWorks && wvp.aggregatedWorks.length === 1) ||
              (!wvp.aggregatedWorks &&
                wvp.filterEntities &&
                wvp.filterEntities.length === 1);

            let aggregatedWorks = wvp.aggregatedWorks;

            if (
              !wvp.aggregatedWorks &&
              wvp.filterEntities &&
              wvp.filterEntities.length === 1
            ) {
              const nonAggregatedWorks = this.getNonAggregatedWorks(
                wvp,
                aggregatedWorks,
              );
              aggregatedWorks = aggregatedWorks.concat(nonAggregatedWorks);
            }

            this.setDataSource(aggregatedWorks);
            break;
          case WorkViewMode.AllFilteredByWorkIds:
            navigateToFirstWork =
              wvp.filterWorkIds && wvp.filterWorkIds.length === 1;
            this.setDataSource(wvp.allWorks);
            break;
        }

        setTimeout(() => {
          switch (wvp.mode) {
            case WorkViewMode.AllFilteredByEntities:
              if (wvp.filterEntities && wvp.filterEntities.length > 0) {
                this.dataContext.resetFilter();
                for (const filterEntity of wvp.filterEntities) {
                  const dsFilter: DataSourceFilter = new DataSourceFilter();
                  dsFilter.objectInfos = [filterEntity];
                  dsFilter.operator = FilterOperator.Or;
                  this.dataContext.applyFilterObject(dsFilter);
                }
              }
              break;
            case WorkViewMode.AggregatedByEntityCategory:
              if (wvp.aggregatedWorks) {
                this.dataContext.resetFilter();
                const dsFilter: DataSourceFilter = new DataSourceFilter();
                dsFilter.type = FilterType.Action;
                dsFilter.displayText =
                  'Objektfolge ' + wvp.pathNameOfAggregation;
                dsFilter.action = () => this.setDataSource(wvp.allWorks);
                this.dataContext.applyFilterObject(dsFilter);
              } else if (wvp.filterEntities && wvp.filterEntities.length > 0) {
                this.dataContext.resetFilter();
                for (const filterEntity of wvp.filterEntities) {
                  const dsFilter: DataSourceFilter = new DataSourceFilter();
                  dsFilter.objectInfos = [filterEntity];
                  dsFilter.operator = FilterOperator.Or;
                  this.dataContext.applyFilterObject(dsFilter);
                }
              }
              break;
            case WorkViewMode.AllFilteredByWorkIds:
              if (wvp.filterWorkIds && wvp.filterWorkIds.length > 0) {
                this.dataContext.resetFilter();
                for (const f of this.dataContext.dataSource.filters.filter(
                  (f) => f.isGlobalPredicate,
                )) {
                  this.dataContext.dataSource.removeFilter(f);
                }
                for (const filterWorkId of wvp.filterWorkIds) {
                  const dsFilter: DataSourceFilter = new DataSourceFilter();
                  dsFilter.type = FilterType.Predicate;
                  dsFilter.predicate = (obj) =>
                    (obj as MobileWorkViewModel).id === filterWorkId;
                  dsFilter.displayText = this.res.get(
                    'ImmediateMasure_FilterText',
                  );
                  dsFilter.value = WorkConstants.FlagFilter_ImmediateMeasure;
                  dsFilter.isGlobalPredicate = true;
                  this.dataContext.applyFilterObject(dsFilter);
                }
              }
              break;
          }
          if (navigateToFirstWork) {
            if (wvp.allWorksFilteredByEntities.length === 1) {
              this.frame.routing
                .navigateName(AppService.Route_WorkDetail)
                .then((succ) => {
                  if (succ) {
                    const context: WorkNavigationContext =
                      new WorkNavigationContext();
                    context.workDataContext = this.dataContext;
                    context.workIndex = 0;
                    this.worksService.refreshNavigationContext(context);
                  }
                });
            } else {
              this.frame.routing.navigateName(AppService.Route_WorkList);
            }
          }
        }, 0);
      }),
    );

    this.subscriptions.push(
      frame.lib.onViewStateChanged().subscribe((args) => {
        switch (args.type) {
          case ViewStateChangeArgType.BottomSheetPanelOpened: {
            worksService.sendDataContext(this.dataContext);
            this.cdr.detectChanges();
            break;
          }
        }
      }),
    );
  }

  trySortFilteredWorks(wvp: WorkViewPackage) {
    if (wvp && wvp.allWorks) {
      wvp.allWorks.sort((a, b) => a.sortOrder - b.sortOrder);
    }
  }

  getNonAggregatedWorks(
    wvp: WorkViewPackage,
    aggregatedWorksForAllEntities: MobileWorkViewModel[],
  ): MobileWorkViewModel[] {
    let ret: MobileWorkViewModel[] = [];
    if (wvp && wvp.allWorks && aggregatedWorksForAllEntities) {
      for (let w of wvp.allWorks) {
        if (
          aggregatedWorksForAllEntities.findIndex(
            (aw) => aw.designation === w.designation,
          ) === -1
        )
          ret.push(w);
      }
    }

    return ret;
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet(
      'abc-maintenance-opera',
    );
  }

  private setDataSource(works: MobileWorkViewModel[]): void {
    if (this.dataSource.rawData !== works) {
      this.dataSource.rawData = works;
      this.dataContext.dataSource = this.dataSource;
      this.dataContext.dataSource.externalFilterCondition = (obj) => {
        const work: MobileWorkViewModel = <MobileWorkViewModel>obj;
        return work?.tasks?.length > 0;
      };
    }
    if (works) {
      this.allAutocompleteItems =
        ViewModelHelper.flattenAndDistinctToArray(works).sort();
    }
  }

  public applyFilter(filter: string): void {
    setTimeout(() => this.searchMenuTrigger.closeMenu(), 0);
    const dsFilter: DataSourceFilter = new DataSourceFilter();
    dsFilter.value = filter;
    this.dataContext.applyFilterObject(dsFilter);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
    this.frameService.dispose();
  }

  ngAfterViewInit() {
    this.animationState = ViewModelHelper.getAnimationData(this.routerOutlet);
    this.cdr.detectChanges();
  }
}
